import { API } from "apis";
import Tag from "components/common/chip/Tag";
import Image from "components/common/images/Image";
import QuoteRequestDialog from "components/dialog/QuoteRequestDialog";
import ItemDetailSideMenu from "components/item/detail/ItemDetailSideMenu";
import { ContextModel, PublicItemDetailModel } from "models";
import { CommonFileModel } from "models/CommonFileModel";
import React from "react";
import { Helmet } from "react-helmet-async";
import { FaRegHeart } from "react-icons/fa";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { Utils } from "utils";

/** 「商品詳細」画面 */
const ItemDetailPage: React.FC = () => {
  const { item_cd } = useParams();

  const ctx = useOutletContext<ContextModel>();
  const didEffect = React.useRef(false);

  const [data, setData] = React.useState<PublicItemDetailModel | null>(null);
  // 自社商品かどうか
  const [isMyItem, setIsMyItem] = React.useState(false);
  const [count, setCount] = React.useState<number | "">(1);
  const [pickup, setPickup] = React.useState<CommonFileModel>();
  const [images, setImages] = React.useState<(CommonFileModel | null)[]>([]);
  const [modalIsOpen, setIsOpen] = React.useState(false);

  // お気に入りに追加
  const onAddFavorite = async (item: PublicItemDetailModel) => {
    ctx.loading.setState(true);
    try {
      // check item has favorite
      if (item.favorite_item_cd) {
        await API.FavoriteItem.del(item_cd!);
      } else {
        await API.FavoriteItem.add(item_cd!);
      }

      setData({...data, favorite_item_cd: item.favorite_item_cd ? null : item_cd} as PublicItemDetailModel);
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  //  PDF資料ダウンロード
  const onDownloadPDF = async () => {
    ctx.loading.setState(true);
    try {
      const response = await API.PublicItem.downloadPDF(item_cd!);
      console.log(response);
      if (response) {
        const blob = new Blob([response]);
        const link = document.createElement("a");
        link.download = "download.pdf";
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  //  商談候補商品に追加
  const onAddCandidate = async (item: PublicItemDetailModel) => {
    ctx.loading.setState(true);
    try {
      const c = count === "" ? 1 : count;

      // check item has candidate
      if (item.candidate_item_cd) {
        await API.CandidateItem.del(item_cd!);
      } else {
        await API.CandidateItem.add(item_cd!, c);
      }

      setData({...data, candidate_item_cd: item.candidate_item_cd ? null : item_cd} as PublicItemDetailModel);
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (didEffect.current) {
      return;
    }
    didEffect.current = true;
    const f = async () => {
      try {
        const auth = await ctx.auth.initialize();

        const response = await API.PublicItem.detail(item_cd!);
        const data = response.data;
        console.log(response);
        setData(data);

        const images = [];
        if (Utils.hasFile(data.item_main_image)) {
          images.push(data.item_main_image);
        }
        if (data.item_sub_image) {
          for (const i of data.item_sub_image) {
            images.push(i);
          }
        }
        while (images.length < 11) {
          images.push(null);
        }
        setImages(images);
        if (images.length > 0 && images[0]) {
          setPickup(images[0]);
        }

        const corp_id = auth.data?.corp_id;
        setIsMyItem(response.data.sale_corp.corp_id === corp_id);
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!item_cd) {
    return <div></div>;
  }

  if (!data) {
    return <div></div>;
  }

  return (
    <div className="flex h-full">
      <Helmet>
        <title>商品詳細</title>
      </Helmet>
      <div className="p-4 flex-1 border-r item-hover">
        {!ctx.auth.data?.is_admin_corp && !isMyItem && (
          <div className="m-4">
            <button
              className={`flex items-center gap-1 secondary px-4 py-2 item-favorite ${
                data.item_cd === data.favorite_item_cd ? "is-favorite" : ""
              }`}
              onClick={() => {
                onAddFavorite(data);
              }}
            >
              <FaRegHeart />
              {data.item_cd === data.favorite_item_cd
                ? "お気に入り済"
                : "お気に入り"}
            </button>
          </div>
        )}

        <div className="mx-4 flex">
          <div className="w-6/12 max-w-[500px]">
            {/* <Image data={data.item_main_image} width="100%" maxHeight="500px" /> */}
            <Image
              data={pickup}
              width="100%"
              height="50vw"
              maxHeight="500px"
              default="item.png"
            />
          </div>
          <div className="w-6/12 px-4 flex flex-col">
            <div className="font-bold text-2xl my-4">{data.item_name}</div>
            <div className="flex-1 flex justify-between items-start">
              {data.is_discretionary_price ? (
                <></>
              ) : (
                <div className="font-bold text-2xl">
                  {Utils.addComma(data.sale_price)}
                  <span className="text-base">円(税抜)</span>
                </div>
              )}

              {data.item_append_pdf && (
                <button className="secondary px-4 py-2" onClick={onDownloadPDF}>
                  PDF資料ダウンロード
                </button>
              )}
            </div>
            {!ctx.auth.data?.is_admin_corp && !isMyItem && (
              <div className="flex my-4 border border-black w-fit rounded-lg">
                <button
                  className={`px-4 py-2 text-xl ${
                    +count <= 1 ? "text-gray" : "text-jhk-blue"
                  }`}
                  onClick={() => {
                    if (count === "") {
                      setCount(1);
                    } else {
                      const tmp = Math.max(1, count - 1);
                      setCount(tmp);
                    }
                  }}
                >
                  -
                </button>
                <div className="px-4 py-2">
                  <input
                    className="w-16 text-center bg-transparent border-none outline-0 rounded-none"
                    value={count}
                    type="tel"
                    onBlur={(e) => {
                      const numericValue =
                        count === "" ? "0" : count.toString();
                      const sanitizedNumericValue = numericValue.replace(
                        /^0+/,
                        "0"
                      );
                      const sanitizedNumericValues =
                        sanitizedNumericValue.replace(/\D/g, "");
                      const parsedValue = sanitizedNumericValues;
                      setCount(parsedValue as any);
                    }}
                    onChange={(e) => {
                      const value = (e.target as any).value;
                      const numericValue = value;
                      const sanitizedValue = numericValue.replace(/^0+/, "");
                      setCount(sanitizedValue !== "" ? sanitizedValue : "0");
                    }}
                  />
                </div>
                <button
                  className={`px-4 py-2 text-xl ${
                    +count >= 9999 ? "text-gray" : "text-jhk-blue"
                  }`}
                  onClick={() => {
                    if (count === "") {
                      setCount(1);
                    } else {
                      const countString = count;
                      const countNumber : number = parseInt(countString as any, 10);
                      const tmp = Math.min(countNumber + 1,9999);
                      setCount(tmp);
                    }
                  }}
                >
                  +
                </button>
              </div>
            )}
            {!ctx.auth.data?.is_admin_corp && !isMyItem && (
              <div className="flex gap-4">
                <button
                  className={`secondary px-4 py-2 item-favorite  ${data.candidate_item_cd ? 'is-favorite' : ''}`}
                  onClick={() => {
                    onAddCandidate(data);
                  }}
                >
                  {data.candidate_item_cd
                    ? "商談候補商品に追加済"
                    : "商談候補商品に追加"}
                </button>
                <button
                  className="primary px-4 py-2"
                  onClick={() => {
                    if (count as number >= 1 && count as number <= 9999) {
                      setIsOpen(true);
                    } else {
                      alert(
                        "数量は1〜9999以内の数字で入力してください"
                      );
                    }
                  }}
                >
                  見積依頼
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="my-4 flex gap-2">
          {images.map((v, i) => (
            <div key={i} className="flex-1">
              {v ? (
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setPickup(v);
                  }}
                >
                  <Image data={v} height="7vw" />
                </div>
              ) : (
                <></>
              )}
            </div>
          ))}
        </div>

        <div className="p-4">
          <div className="border-b-2 border-b-jhk-blue mb-4">
            <span className="text-2xl font-bold">紹介文</span>
          </div>
          <div className="whitespace-pre-line break-words break-all">
            {data.item_description}
          </div>
        </div>
        {data.item_tag && (
          <div className="p-4">
            <div className="border-b-2 border-b-jhk-blue mb-4">
              <span className="text-2xl font-bold">
                この商品につけられたタグ
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {data.item_tag.map((tag, i) => {
                return (
                  <Link
                    key={i}
                    to={`/item-list/?keyword=${tag.item_tag_name}&get_count=10`}
                  >
                    <Tag text={tag.item_tag_name} />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="w-80 h-full">
        <ItemDetailSideMenu
          showButtons={!ctx.auth.data?.is_admin_corp && !isMyItem}
          ctx={ctx}
          corp={data.sale_corp}
        />
        {data.recommend_item && (
          <>
            <div className="p-4">
              <div className="text-xl font-bold">関連商品</div>
              <div className="grid grid-cols-2 gap-2">
                {data.recommend_item.slice(0, 6).map((item, index) => {
                  return (
                    <a
                      key={index}
                      href={`${process.env.PUBLIC_URL}/item-detail/${item.item_cd}/`}
                    >
                      {/* <Link key={index} to={`/item-detail/${item.item_cd}/`}> */}
                      <div className="p-2">
                        <Image data={item.item_main_image} default="item.png" />
                        <div
                          className="font-bold text-sm break-all line-clamp-1"
                          title={item.item_name}
                        >
                          {item.item_name}
                        </div>
                        {!item.is_discretionary_price && (
                          <div className="font-bold">
                            {Utils.addComma(item.sale_price)}
                            <span className="text-xs">円(税抜)</span>
                          </div>
                        )}
                      </div>
                      {/* </Link> */}
                    </a>
                  );
                })}
              </div>
            </div>
            <div className="px-4 pb-8">
              <Link
                to={
                  !ctx.auth.data?.is_admin_corp &&
                  data.sale_corp.corp_id === ctx.auth.data?.corp_id
                    ? "/exhibit-list?get_count=10"
                    : `/item-list?keyword=${data.sale_corp.corp_name}&corp_id=${data.sale_corp.corp_id}&get_count=10`
                }
              >
                <button className="secondary w-full py-2">
                  全{data.sale_item_max_count}商品を見る
                </button>
              </Link>
            </div>
          </>
        )}
      </div>
      <QuoteRequestDialog
        ctx={ctx}
        modalIsOpen={modalIsOpen}
        targets={[
          {
            corp_id: data.sale_corp.corp_id,
            corp_name: data.sale_corp.corp_name,
            candidate_item_list: [
              {
                candidate_item_cd: item_cd,
                quantity: count === "" ? 1 : count,
                item_name: data.item_name,
                sale_price: data.sale_price,
                item_main_image: {
                  file_data: "",
                  file_name: "",
                  image_url: "",
                },
                is_discretionary_price: data.is_discretionary_price,
              },
            ],
          },
        ]}
        close={(result) => setIsOpen(false)}
      />
    </div>
  );
};
export default ItemDetailPage;
